// src/stores/brandStore.js
import { create } from 'zustand';
import * as brandService from '../services/brandService';
import useNotificationStore from './notificationStore';

const useBrandStore = create((set, get) => ({
  // State
  brands: [],
  totalBrands: 0,
  currentBrand: null,
  canViewCredentials: false,  // Store the permission flag
  isLoading: false,
  criticalError: null,        // Only for brand loading failures
  operationError: null,       // For non-critical operations
  meta: {},
  
  // Fetch brands with pagination and filters
  fetchBrands: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrands(params);
      set({
        brands: response.data.brands,
        totalBrands: response.data.totalCount,
        meta: response.meta,
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch brands'
      });
      console.log('error', error);
      // Don't throw error - return empty data
      return { brands: [], totalCount: 0 };
    }
  },
  
  // Fetch brands for a specific client
  fetchClientBrands: async (clientId, params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getClientBrands(clientId, params);
      set({
        brands: response.data.brands,
        totalBrands: response.data.totalCount,
        meta: response.meta,
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch client brands'
      });
      console.log('error', error);
      // Don't throw error - return empty data
      return { brands: [], totalCount: 0 };
    }
  },
  
  // Fetch a single brand by ID
  fetchBrand: async (brandId) => {
    set({ isLoading: true, criticalError: null, currentBrand: null });
    try {
      const response = await brandService.getBrand(brandId);
      set({
        currentBrand: response.data.brand,
        canViewCredentials: response.data.canViewCredentials || false,
        isLoading: false
      });
      return response.data.brand;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch brand details'
      });
      console.log('error', error);
      // Don't throw error - return null
      return null;
    }
  },
  
  // Create a new brand
  createBrand: async (brandData) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.createBrand(brandData);
      // Add the new brand to the list and increment total count
      set(state => ({
        brands: [response.data.brand, ...state.brands],
        totalBrands: state.totalBrands + 1,
        isLoading: false
      }));
      
      // Success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Brand created successfully');
      
      return response.data.brand;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to create brand'
      });
      
      // Error notification
      const { showError } = useNotificationStore.getState();
      showError(error.message || 'Failed to create brand');
      
      console.log('error', error);
      throw error;
    }
  },
  
  // Update an existing brand
  updateBrand: async (brandId, brandData) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrand(brandId, brandData);
      // Update the brand in the list
      set(state => ({
        brands: state.brands.map(brand => 
          brand._id === brandId ? response.data.brand : brand
        ),
        currentBrand: response.data.brand,
        isLoading: false
      }));
      
      // Success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Brand updated successfully');
      
      return response.data.brand;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to update brand'
      });
      
      // Error notification
      const { showError } = useNotificationStore.getState();
      showError(error.message || 'Failed to update brand');
      
      console.log('error', error);
      throw error;
    }
  },
  
  // Delete a brand
  deleteBrand: async (brandId) => {
    set({ isLoading: true, criticalError: null });
    try {
      await brandService.deleteBrand(brandId);
      // Remove the brand from the list and decrement total count
      set(state => ({
        brands: state.brands.filter(brand => brand._id !== brandId),
        totalBrands: state.totalBrands - 1,
        isLoading: false
      }));
      
      // Success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Brand deleted successfully');
      
      return true;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to delete brand'
      });
      
      // Error notification
      const { showError } = useNotificationStore.getState();
      showError(error.message || 'Failed to delete brand');
      
      console.log('error', error);
      throw error;
    }
  },
  
  // Upload brand logo
  uploadLogo: async (brandId, logoType, logoFile) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.uploadBrandLogo(brandId, logoType, logoFile);
      
      // Create updated logo object
      const updatedLogo = {
        ...(get().currentBrand?.logo || {}),
        [logoType]: response.data.logo
      };
      
      // Update brand with new logo info
      set(state => ({
        brands: state.brands.map(brand => 
          brand._id === brandId 
            ? { ...brand, logo: updatedLogo } 
            : brand
        ),
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, logo: updatedLogo }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.logo;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to upload brand logo'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Upload brand asset
  uploadAsset: async (brandId, assetFile, metadata = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.uploadBrandAsset(brandId, assetFile, metadata);
      
      // Update brand with new asset
      const updatedAssets = [
        ...(get().currentBrand?.assets || []),
        response.data.asset
      ];
      
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, assets: updatedAssets }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.asset;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to upload brand asset'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Get brand assets
  fetchBrandAssets: async (brandId) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrandAssets(brandId);
      
      // Update brand with assets
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, assets: response.data.assets }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.assets;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch brand assets'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Delete brand asset
  deleteAsset: async (brandId, assetId) => {
    set({ isLoading: true, criticalError: null });
    try {
      await brandService.deleteBrandAsset(brandId, assetId);
      
      // Remove asset from brand
      if (get().currentBrand && get().currentBrand._id === brandId) {
        const updatedAssets = get().currentBrand.assets.filter(
          asset => asset._id !== assetId
        );
        
        set(state => ({
          currentBrand: {
            ...state.currentBrand,
            assets: updatedAssets
          },
          isLoading: false
        }));
      } else {
        set({ isLoading: false });
      }
      
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to delete brand asset'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Fetch brand campaigns
  fetchBrandCampaigns: async (brandId, params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrandCampaigns(brandId, params);
      
      // Update brand with campaigns
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, campaigns: response.data.campaigns }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.campaigns;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch brand campaigns'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Fetch brand content
  fetchBrandContent: async (brandId, params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrandContent(brandId, params);
      
      // Update brand with content
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, content: response.data.content }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.content;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to fetch brand content'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Update brand colors
  updateColors: async (brandId, colors) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrandColors(brandId, colors);
      
      // Update brand with new colors
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, colors: response.data.colors }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.colors;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to update brand colors'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Update brand fonts
  updateFonts: async (brandId, fonts) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrandFonts(brandId, fonts);
      
      // Update brand with new fonts
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, fonts: response.data.fonts }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.fonts;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to update brand fonts'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Update brand voice
  updateVoice: async (brandId, voiceData) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrandVoice(brandId, voiceData);
      
      // Update brand with new voice settings
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, voice: response.data.voice }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.voice;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to update brand voice'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Update brand social accounts
  updateBrandSocialAccounts: async (brandId, socialAccounts) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrandSocialAccounts(brandId, socialAccounts);
      
      // Update brand with new social accounts
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, socialAccounts: response.data.socialAccounts }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.socialAccounts;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update brand social accounts'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Update brand competitors
  updateBrandCompetitors: async (brandId, competitors) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.updateBrandCompetitors(brandId, competitors);
      
      // Update brand with new competitors
      set(state => ({
        currentBrand: state.currentBrand && state.currentBrand._id === brandId
          ? { ...state.currentBrand, competitors: response.data.competitors }
          : state.currentBrand,
        isLoading: false
      }));
      
      return response.data.competitors;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update brand competitors'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Get brand analytics 
  fetchBrandAnalytics: async (brandId, params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrandAnalytics(brandId, params);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch brand analytics'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Get brand usage statistics
  fetchBrandUsageStats: async (brandId) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await brandService.getBrandUsageStats(brandId);
      set({ isLoading: false });
      return response.data.stats;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch brand usage stats'
      });
      console.log('error', error);
      throw error;
    }
  },
  
  // Clear current brand
  clearCurrentBrand: () => {
    set({ currentBrand: null });
  },
  
  // Clear critical error
  clearError: () => {
    set({ criticalError: null });
  },
  
  // Clear operation error
  clearOperationError: () => {
    set({ operationError: null });
  }
}));

export default useBrandStore;